import { getUserId } from "shared-frontend/functions/auth";

import { useGetSubscriptionSummary } from "../api/generated/customer/customer";

/**
 * The summary as it reads before the query resolves, on error, and when there is no customer to ask
 * about.
 *
 * These are exactly the values the selectors this hook replaces produced from an empty
 * `state.entities.subscriptions` — the state the app was in before the bootstrap fetch resolved.
 * Keeping them identical is what makes removing the app-wide render gate invisible: the first frame
 * renders the same as it always did. It also means no consumer needs a loading branch or a null
 * check, so `isRequired` number propTypes stay satisfied throughout.
 */
const EMPTY_SUMMARY = Object.freeze( {
	total: 0,
	liveCount: 0,
	activeCount: 0,
	isOnlyProvisioner: false,
	hasMixed: false,
	provisionerNames: Object.freeze( [] ),
	assignableUnusedSlotsCount: 0,
	totalActiveLicenses: 0,
	upcomingRenewalCount: 0,
	hasThirdPartyLicenses: false,
} );

/**
 * Account-wide subscription aggregates, from `GET Customers/:id/subscriptions/summary`.
 *
 * Replaces the selectors that reduced the whole subscription collection to a boolean or an integer,
 * and with them the unbounded fetch that collection required. Every consumer shares one query key,
 * so a page rendering several of them issues a single request.
 *
 * Deliberately holds no `useEffect`: this is a read, so there is nothing to mirror into Redux and
 * nothing to report to the data layer. Analytics belong on the mutation that caused the event, never
 * on a query result, which reflects cache state rather than something having happened.
 *
 * @returns {{summary: Object, isLoading: boolean, isError: boolean}} The aggregates and query state.
 */
export default function useSubscriptionSummary() {
	const customerId = getUserId();

	const { data, isLoading, isError } = useGetSubscriptionSummary( customerId, {
		query: {
			// `getUserId()` returns an empty string when there is no access token, and the generated
			// `enabled` only guards null and undefined — so without this the hook would request
			// `Customers//subscriptions/summary`.
			enabled: Boolean( customerId ),
		},
	} );

	return {
		summary: data ?? EMPTY_SUMMARY,
		isLoading,
		isError,
	};
}
